import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  RefreshCw,
  ChevronRight,
  Folder,
  FileText,
  FileSpreadsheet,
  File,
  Presentation,
  Image,
  Archive,
  Music,
  Video,
  Code,
  FileCode,
  Download,
  Inbox,
  Clock,
  Link,
  Clipboard,
  ClipboardCheck,
} from 'lucide-react';
import type { DriveItem } from '@/shared';

interface PathSegment {
  id: string | null; // null for space root
  name: string;
}

interface FolderBrowserProps {
  spaceName: string;
  spaceSlug: string;
  items: DriveItem[];
  pathHistory: PathSegment[];
  isAdmin: boolean;
  lastSyncedAt?: string | null;
  onNavigateFolder: (folderId: string | null, folderName: string) => void;
  onPreviewFile: (file: DriveItem) => void;
  onBackToDashboard: () => void;
  onRefreshFolder: (folderId: string | null, folderName: string) => void;
}

function formatSize(bytes?: string) {
  if (!bytes) return '--';
  const num = parseInt(bytes, 10);
  if (isNaN(num)) return '--';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  return `${(num / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '--';
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatRelativeTime(dateStr?: string | null): string {
  if (!dateStr) return 'Never synced';
  const time = new Date(dateStr).getTime();
  if (isNaN(time)) return 'Never synced';
  const now = Date.now();
  const diffSec = Math.floor((now - time) / 1000);

  if (diffSec < 10) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

interface FileIconInfo {
  icon: React.ReactNode;
  className: string;
}

function getFileIconInfo(item: DriveItem): FileIconInfo {
  if (item.isFolder) {
    return { icon: <Folder size={20} />, className: 'folder' };
  }

  const mime = (item.mimeType || '').toLowerCase();
  const name = (item.name || '').toLowerCase();

  // PDF
  if (mime === 'application/pdf' || name.endsWith('.pdf')) {
    return { icon: <File size={20} />, className: 'file-pdf' };
  }
  // Spreadsheet / Sheets
  if (
    mime.includes('spreadsheet') ||
    mime.includes('excel') ||
    mime.includes('csv') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.xls') ||
    name.endsWith('.csv')
  ) {
    return { icon: <FileSpreadsheet size={20} />, className: 'file-sheet' };
  }
  // Document / Word
  if (
    mime.includes('document') ||
    mime.includes('word') ||
    name.endsWith('.docx') ||
    name.endsWith('.doc') ||
    name.endsWith('.txt')
  ) {
    return { icon: <FileText size={20} />, className: 'file-doc' };
  }
  // Presentation / Slides
  if (
    mime.includes('presentation') ||
    mime.includes('powerpoint') ||
    name.endsWith('.pptx') ||
    name.endsWith('.ppt')
  ) {
    return { icon: <Presentation size={20} />, className: 'file-presentation' };
  }
  // Image
  if (mime.startsWith('image/') || name.match(/\.(png|jpe?g|gif|svg|webp)$/)) {
    return { icon: <Image size={20} />, className: 'file-image' };
  }
  // Archive
  if (mime.includes('zip') || mime.includes('compressed') || name.match(/\.(zip|tar|gz|rar|7z)$/)) {
    return { icon: <Archive size={20} />, className: 'file-archive' };
  }
  // Audio
  if (mime.startsWith('audio/') || name.match(/\.(mp3|wav|ogg|m4a|flac)$/)) {
    return { icon: <Music size={20} />, className: 'file-audio' };
  }
  // Video
  if (mime.startsWith('video/') || name.match(/\.(mp4|mkv|avi|mov|wmv)$/)) {
    return { icon: <Video size={20} />, className: 'file-video' };
  }
  // Code
  if (
    mime.includes('json') ||
    mime.includes('javascript') ||
    mime.includes('typescript') ||
    name.match(/\.(json|ts|tsx|js|jsx|html|css|scss|md|py|go|sh)$/)
  ) {
    return { icon: <FileCode size={20} />, className: 'file-code' };
  }

  // Default File
  return { icon: <FileText size={20} />, className: 'file' };
}

const API_URL = '';

export function FolderBrowser({
  spaceName,
  spaceSlug,
  items,
  pathHistory,
  isAdmin,
  lastSyncedAt,
  onNavigateFolder,
  onPreviewFile,
  onBackToDashboard,
  onRefreshFolder,
}: FolderBrowserProps) {
  const [breadcrumbsExpanded, setBreadcrumbsExpanded] = useState(false);
  const [copyUrlTooltip, setCopyUrlTooltip] = useState(false);
  const [copyLocationTooltip, setCopyLocationTooltip] = useState(false);

  useEffect(() => {
    setBreadcrumbsExpanded(false);
  }, [pathHistory]);

  const currentSegment = pathHistory.length > 0 ? pathHistory[pathHistory.length - 1] : null;

  const handleCopyUrl = async () => {
    const currentFolderId = currentSegment?.id || null;
    const origin = window.location.origin;
    const deepLinkUrl = currentFolderId
      ? `${origin}/${spaceSlug}/folder/${currentFolderId}`
      : `${origin}/${spaceSlug}`;

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(deepLinkUrl);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = deepLinkUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopyUrlTooltip(true);
      setTimeout(() => setCopyUrlTooltip(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleCopyLocation = async () => {
    const locationPath = ['Hub', ...pathHistory.map(seg => seg.name)].join(' / ');
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(locationPath);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = locationPath;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopyLocationTooltip(true);
      setTimeout(() => setCopyLocationTooltip(false), 2000);
    } catch (err) {
      console.error('Failed to copy location:', err);
    }
  };

  const parentSegment = pathHistory.length >= 2 ? pathHistory[pathHistory.length - 2] : null;
  const isSubfolder = pathHistory.length >= 2;

  const handleBackClick = () => {
    if (isSubfolder && parentSegment) {
      onNavigateFolder(parentSegment.id, parentSegment.name);
    } else {
      onBackToDashboard();
    }
  };

  const backButtonText = isSubfolder
    ? `Back to ${parentSegment?.name || 'Parent Folder'}`
    : 'Back to Dashboard';

  return (
    <div className="glass-panel animate-fade-in">
      {/* Header bar */}
      <div className="folder-browser-header">
        <div>
          <button className="btn btn-secondary" onClick={handleBackClick} style={{ padding: '0.4rem 0.8rem', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            <ArrowLeft size={14} /> <span className="desktop-only-text">{backButtonText}</span>
          </button>
          <h2 style={{ fontSize: '1.8rem', fontFamily: 'Outfit, sans-serif' }}>{spaceName}</h2>
        </div>
        
        {isAdmin && (
          <button
            className="btn btn-secondary refresh-cache-btn"
            onClick={() => onRefreshFolder(currentSegment?.id || null, currentSegment?.name || spaceName)}
            title="Synchronize this folder and its subfolders with Google Drive"
          >
            <RefreshCw size={14} /> <span className="desktop-only-text">Refresh folder</span>
          </button>
        )}
      </div>

      {/* Breadcrumbs & Last Synced Badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <nav aria-label="breadcrumb" style={{ margin: 0 }}>
            <ul className="breadcrumbs" style={{ margin: 0 }}>
              <li className="breadcrumb-item">
                <a href="#dashboard" onClick={(e) => { e.preventDefault(); onBackToDashboard(); }}>Hub</a>
                <ChevronRight size={12} className="breadcrumb-separator" />
              </li>
              
              {!breadcrumbsExpanded && pathHistory.length >= 2 ? (
                <>
                  <li className="breadcrumb-item">
                    <button 
                      className="breadcrumb-expand-btn" 
                      onClick={() => setBreadcrumbsExpanded(true)}
                      title="Expand breadcrumbs"
                    >
                      …
                    </button>
                    <ChevronRight size={12} className="breadcrumb-separator" />
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {pathHistory[pathHistory.length - 1].name}
                  </li>
                </>
              ) : (
                pathHistory.map((seg, idx) => {
                  const isLast = idx === pathHistory.length - 1;
                  if (isLast) {
                    return (
                      <li key={idx} className="breadcrumb-item active" aria-current="page">
                        {seg.name}
                      </li>
                    );
                  }
                  return (
                    <li key={idx} className="breadcrumb-item">
                      <a
                        href={`#folder-${seg.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          onNavigateFolder(seg.id, seg.name);
                        }}
                      >
                        {seg.name}
                      </a>
                      <ChevronRight size={12} className="breadcrumb-separator" />
                    </li>
                  );
                })
              )}
            </ul>
          </nav>

          <button
            className="btn btn-secondary"
            onClick={handleCopyUrl}
            title="Copy folder URL for sharing"
            style={{
              padding: '0.4rem 0.8rem',
              fontSize: '0.8rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
            }}
          >
            {copyUrlTooltip ? <ClipboardCheck size={14} /> : <Link size={14} />}
            <span>{copyUrlTooltip ? 'Copied URL!' : 'Copy URL'}</span>
          </button>

          <button
            className="btn btn-secondary"
            onClick={handleCopyLocation}
            title="Copy folder readable location path"
            style={{
              padding: '0.4rem 0.8rem',
              fontSize: '0.8rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.35rem',
            }}
          >
            {copyLocationTooltip ? <ClipboardCheck size={14} /> : <Clipboard size={14} />}
            <span>{copyLocationTooltip ? 'Copied Location!' : 'Copy Location'}</span>
          </button>
        </div>

        <span
          className="last-synced-badge"
          style={{
            fontSize: '0.8rem',
            color: 'var(--text-secondary, #888)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            padding: '0.2rem 0.6rem',
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
          title={lastSyncedAt ? `Exact ISO time: ${lastSyncedAt}` : 'Folder has not been synchronized yet'}
        >
          <Clock size={13} style={{ opacity: 0.7 }} />
          <span>Last synced {formatRelativeTime(lastSyncedAt)}</span>
        </span>
      </div>

      {/* Item List */}
      {items.length === 0 ? (
        <div className="empty-state">
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem', color: 'var(--text-muted)' }}>
            <Inbox size={40} />
          </div>
          <p>This folder is empty.</p>
        </div>
      ) : (
        <div className="items-list">
          {items.map((item, index) => {
            const downloadUrl = `${API_URL}/api/files/${item.id}/download`;
            const iconInfo = getFileIconInfo(item);
            return (
              <div
                key={item.id}
                className="item-row animate-slide-up"
                style={{ animationDelay: `${index * 40}ms` }}
                onClick={() => {
                  if (item.isFolder) {
                    onNavigateFolder(item.id, item.name);
                  } else {
                    onPreviewFile(item);
                  }
                }}
              >
                <div className="item-main">
                  <span className={`item-icon ${iconInfo.className}`}>
                    {iconInfo.icon}
                  </span>
                  <div className="item-details">
                    <span className="item-name" title={item.name}>{item.name}</span>
                    <div className="item-meta-mobile">
                      {item.isFolder ? 'Folder' : formatSize(item.size)} &middot; {formatDate(item.modifiedTime)}
                    </div>
                  </div>
                </div>

                <div className="item-meta-desktop">
                  <span className="item-size">
                    {item.isFolder ? 'Folder' : formatSize(item.size)}
                  </span>
                  <span className="item-date">
                    {formatDate(item.modifiedTime)}
                  </span>
                </div>

                <div className="item-actions" onClick={(e) => e.stopPropagation()}>
                  {!item.isFolder && (
                    <a
                      href={downloadUrl}
                      className="icon-btn"
                      title="Download file"
                      download
                    >
                      <Download size={16} />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
